import { computed, toValue, type MaybeRef } from 'vue';
import { useI18n } from './useI18n';
import { useIntercom } from './useIntercom';
import { useStore } from '@/store';
import { switchDesktopMobileView } from '@/routes/hooks/desktop-view-forced';
import { saveUserDataToLocalStorage } from '@/services/localStorage-users-data';
import config from '@/config';

export type UseNavbarSupportOptions = {
  hiddenElements?: MaybeRef<string[]>;
  hasCloseIntercom?: MaybeRef<boolean>;
  onTemporaryPassword?: () => void;
};

export const useNavbarSupport = (options: UseNavbarSupportOptions = {}) => {
  const store = useStore();
  const { t, locale } = useI18n();
  const { openLiveSupport, closeLiveSupport } = useIntercom();

  const helpCenterLink = computed(() => `https://docs.bemob.com/${locale.value}/home`);

  const isIntercomOpen = computed(() => store.liveChatOpen);

  const toggleMobileDesktopViewForced = () => {
    const newValue = !store.desktopViewForced;
    store.setDesktopViewForced(newValue);
    saveUserDataToLocalStorage(store.user.id, { desktopViewForced: newValue });
    switchDesktopMobileView(newValue);
  };

  const toggleQuickStart = () => {
    store.toggleTutorialQuickStart();
  };

  const menuList = computed((): Bemob.ListItem[] => {
    const hiddenElements = toValue(options.hiddenElements) ?? [];
    const hasCloseIntercom = toValue(options.hasCloseIntercom) ?? false;

    return [
      {
        key: 'openLiveSupport',
        title: t('Open live chat'),
        iconAppend: 'fas fa-question',
        showCondition: !hiddenElements.includes('openLiveSupport') && !isIntercomOpen.value,
        click: () => openLiveSupport(),
      },
      {
        key: 'closeLiveSupport',
        title: t('Close live chat'),
        iconAppend: 'fas fa-times',
        showCondition: hasCloseIntercom && isIntercomOpen.value,
        click: () => closeLiveSupport(),
      },
      {
        key: 'helpCenterLink',
        title: t('Help center'),
        iconAppend: 'fas fa-book',
        href: helpCenterLink.value,
        target: '_blank',
      },
      {
        key: 'quickStart',
        title: t('Quick start'),
        iconAppend: 'fa fa-life-ring',
        click: () => toggleQuickStart(),
      },
      {
        key: 'blog',
        title: t('Blog'),
        iconAppend: 'far fa-newspaper',
        href: config.blogLink,
        target: '_blank',
      },
      {
        key: 'createTemporaryPassword',
        title: t('Create temporary password'),
        iconAppend: 'fas fa-ellipsis-h',
        click: () => options.onTemporaryPassword?.(),
      },
      {
        key: 'forceDesktopView',
        title: t('Desktop view'),
        iconAppend: 'fa fa-desktop',
        click: () => toggleMobileDesktopViewForced(),
        showCondition: store.desktopViewForced === false,
      },
      {
        key: 'forceMobileView',
        title: t('Mobile view'),
        iconAppend: 'fa fa-mobile',
        click: () => toggleMobileDesktopViewForced(),
        showCondition: store.desktopViewForced === true,
      },
    ];
  });

  const menuListFiltered = computed(() =>
    menuList.value.filter(
      (item) => typeof item.showCondition === 'undefined' || item.showCondition,
    ),
  );

  return {
    helpCenterLink,
    isIntercomOpen,
    menuList,
    menuListFiltered,
    openLiveSupport,
    closeLiveSupport,
    toggleMobileDesktopViewForced,
    toggleQuickStart,
  };
};
